export type OnboardingMode = "guided" | "legacy_ready";

export interface OnboardingState {
  currentStep: number;
  completedSteps: number[];
  stepData: Record<string, Record<string, unknown>>;
  revision: number;
  status: "in_progress" | "completed";
  mode: OnboardingMode;
  updatedAt?: string;
}

export const onboardingSteps = [
  { id: 1, title: "网站信息", summary: "告诉我们网站是什么" },
  { id: 2, title: "安装", summary: "选择最适合的接入方式" },
  { id: 3, title: "自动验证", summary: "确认网页与 Runtime 可连接" },
  { id: 4, title: "建立知识", summary: "同步公开内容作为回答依据" },
  { id: 5, title: "配置能力", summary: "从安全的默认能力开始" },
  { id: 6, title: "测试", summary: "用真实问题检查体验" },
  { id: 7, title: "发布", summary: "先仅向站长灰度" },
] as const;

export const defaultStepData: Record<number, Record<string, unknown>> = {
  1: { name: "", primaryUrl: "", siteType: "content", purpose: "为访客回答网站内容问题" },
  2: { installMethod: "script" },
  3: { manualContinue: false },
  4: { publicSync: true, visitorCrawl: false },
  5: { answerEnabled: true, operationMode: "advice" },
  6: { testConfirmed: false },
  7: { audience: "owner" },
};

export function normalizeOnboarding(input: Partial<OnboardingState> | undefined): OnboardingState {
  const completed = Array.from(new Set(input?.completedSteps || []))
    .filter((step) => step >= 1 && step <= 7)
    .sort((a, b) => a - b);
  const currentStep = Math.min(7, Math.max(1, Number(input?.currentStep) || 1));
  return {
    currentStep,
    completedSteps: completed,
    stepData: input?.stepData || {},
    revision: Math.max(0, Number(input?.revision) || 0),
    status: input?.status === "completed" ? "completed" : "in_progress",
    mode: input?.mode === "legacy_ready" ? "legacy_ready" : "guided",
    updatedAt: input?.updatedAt,
  };
}

export function canOpenOnboardingStep(state: OnboardingState, step: number): boolean {
  return step <= state.currentStep || state.completedSteps.includes(step);
}

export function mergeStepDraft(
  state: OnboardingState,
  step: number,
  localDraft?: Record<string, unknown>
): Record<string, unknown> {
  return {
    ...(defaultStepData[step] || {}),
    ...(state.stepData[String(step)] || {}),
    ...(localDraft || {}),
  };
}

export function onboardingDraftKey(siteId: string, step: number): string {
  return `hongmo:onboarding:${siteId || "new"}:${step}`;
}

export function installSnippet(siteId: string, method: string): string {
  const safeSiteId = siteId || "创建网站后自动生成";
  if (method === "npm") return `npm install @hongmoai/site-sdk\nHongMoAI.init({ siteId: "${safeSiteId}" });`;
  if (method === "server") return `Authorization: Bearer <runtime-token>\nX-HongMo-Site: ${safeSiteId}`;
  if (method === "coding-agent") return `请 Coding Agent 安装洪墨 AI Site SDK，siteId 为 ${safeSiteId}`;
  if (method === "cms") return `在 CMS 插件中填写 Site ID：${safeSiteId}`;
  return `<script async src="https://cdn.hongmo.ai/site.js" data-site-id="${safeSiteId}"></script>`;
}
